import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface FeedbackProvider {
  getId: () => string;
  identifyUser: (userId: string, metadata?: any) => void;
  isInitialized: () => boolean;
  showFeedbackModal: () => void;
}

interface FeedbackContextType {
  registerFeedbackProvider: (provider: FeedbackProvider) => void;
  identifyUser: (userId: string, metadata?: any) => void;
  showFeedbackModal: () => void;
  isFeedbackProviderInitialized: boolean;
  feedbackProvider: FeedbackProvider | undefined;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(
  undefined,
);

const INITIALIZATION_CHECK_INTERVAL = 500;

export const FeedbackProvider = ({ children }: { children: ReactNode }) => {
  const [feedbackProvider, setFeedbackProvider] = useState<FeedbackProvider>();
  const [initializedLastCheckedAt, setInitializedLastCheckedAt] =
    useState<number>();
  const [isInitialized, setIsInitialized] = useState(false);

  // Feedback button gets injected into the page by Logrocket at some point after the page loads
  // This effect will make sure it becomes usable as soon as it's available
  useEffect(() => {
    if (isInitialized) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsInitialized(feedbackProvider?.isInitialized() ?? false);
      setInitializedLastCheckedAt(Date.now());
    }, INITIALIZATION_CHECK_INTERVAL);

    return () => clearTimeout(timeout);
  }, [isInitialized, initializedLastCheckedAt]);

  const registerFeedbackProvider = (provider: FeedbackProvider) => {
    if (feedbackProvider?.getId() === provider.getId()) {
      // (don't spam, since we're registering them outside effect)
      // console.warn('Feedback provider already registered. Replacing...');
      return;
    }

    setFeedbackProvider(provider);
  };

  const identifyUser = (userId: string, metadata?: any) => {
    feedbackProvider?.identifyUser(userId, metadata);
  };

  const showFeedbackModal = () => {
    feedbackProvider?.showFeedbackModal();
  };

  return (
    <FeedbackContext.Provider
      value={{
        registerFeedbackProvider,
        feedbackProvider,
        identifyUser,
        showFeedbackModal,
        isFeedbackProviderInitialized: isInitialized,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
};
